import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    ViewEncapsulation,
    afterRenderEffect,
    inject,
    input,
    signal
} from '@angular/core';
import { REDUCED_MOTION_QUERY } from '../accessibility/reduced-motion';

export type SpinnerType = 'infinitform' | 'simple';
export type SpinnerSize = 'default' | 'small' | 'tiny' | 'large';

/**
 * Loading indicator. `infinitform` is the solid logo mark with a short glint
 * running around its outer edge; at `large` it is the still full-screen
 * lockup (mark and wordmark, no glint). `simple` is a ring.
 * The host is the status region; the drawing itself is hidden from assistive tech.
 */
@Component({
    selector: 'app-spinner',
    templateUrl: './spinner.component.html',
    styleUrl: './spinner.component.scss',
    encapsulation: ViewEncapsulation.ShadowDom,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        role: 'status',
        '[attr.aria-label]': 'ariaLabel()',
        '[class.type-infinitform]': 'type() === "infinitform"',
        '[class.type-simple]': 'type() === "simple"',
        '[class.size-small]': 'size() === "small"',
        '[class.size-tiny]': 'size() === "tiny"',
        '[class.size-large]': 'size() === "large"',
        '[class.reduced-motion]': 'reducedMotion()'
    }
})
export class SpinnerComponent {
    readonly type = input<SpinnerType>('infinitform');
    readonly size = input<SpinnerSize>('default');
    /** Accessible name of the status region. */
    readonly ariaLabel = input('Loading', { alias: 'aria-label' });

    /** Follows the OS setting live; the logo then stays still, without the glint, and shows a text label. */
    readonly reducedMotion = signal(false);

    private readonly destroyRef = inject(DestroyRef);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    constructor() {
        this.watchReducedMotion();
        afterRenderEffect(() => {
            this.type();
            this.reducedMotion();
            this.lockGlintPhase();
        });
    }

    /** Starts the glint at document time zero, so every logo spinner on the page moves in step. */
    private lockGlintPhase(): void {
        const root = this.host.nativeElement.shadowRoot ?? this.host.nativeElement;
        root.querySelectorAll('.glint').forEach((glint) => {
            if (typeof glint.getAnimations !== 'function') {
                return;
            }
            glint.getAnimations().forEach((animation) => {
                animation.startTime = 0;
            });
        });
    }

    private watchReducedMotion(): void {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return;
        }
        const query = window.matchMedia(REDUCED_MOTION_QUERY);
        if (!query) {
            return;
        }
        this.reducedMotion.set(query.matches);
        const onChange = (event: MediaQueryListEvent): void => this.reducedMotion.set(event.matches);
        query.addEventListener?.('change', onChange);
        this.destroyRef.onDestroy(() => query.removeEventListener?.('change', onChange));
    }
}
